package kz.dentalcrm.app.screens.protocols.detail

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronDown
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.Plus
import compose.icons.feathericons.Trash
import compose.icons.feathericons.X
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add_option
import dentalcrm.composeapp.generated.resources.add_question
import dentalcrm.composeapp.generated.resources.protocol_delete_option
import dentalcrm.composeapp.generated.resources.protocol_delete_question
import dentalcrm.composeapp.generated.resources.protocol_edit_title
import dentalcrm.composeapp.generated.resources.protocol_option_label
import dentalcrm.composeapp.generated.resources.protocol_options_label
import dentalcrm.composeapp.generated.resources.protocol_question_number
import dentalcrm.composeapp.generated.resources.protocol_question_text
import dentalcrm.composeapp.generated.resources.save
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UISelfTextInput
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kz.dentalcrm.app.navigation.AppNavigator
import org.koin.compose.koinInject

@Composable
fun DetailProtocolScreen(
    protocolId: String,
    viewModel: DetailProtocolViewModel = koinViewModel()
) {
    val state by viewModel.viewState.collectAsState()
    val navigator: AppNavigator = koinInject()

    LaunchedEffect(Unit) {
        viewModel.setEvent(DetailProtocolEvent.LoadData(protocolId))
    }

    LaunchedEffect(viewModel.effect) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is DetailProtocolEffect.NavigateBack -> navigator.navigateUp()
                is DetailProtocolEffect.ShowError -> {
                    // Handle error (e.g. show toast)
                }

                is DetailProtocolEffect.ShowSuccess -> {
                    // Handle success
                }
            }
        }
    }

    UiStateScaffold(
        modifier = Modifier.fillMaxSize(),
        isLoading = state.isLoading,
        content = {
            Column(modifier = Modifier.fillMaxSize()) {
                ContentHeader(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                    text = stringResource(Res.string.protocol_edit_title),
                    leadingContent = {
                        UIIconBackButton(
                            onClick = {
                                viewModel.setEvent(DetailProtocolEvent.OnBack)
                            }
                        )
                    }
                )

                LazyColumn(
                    modifier = Modifier.weight(1f).padding(horizontal = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    itemsIndexed(
                        state.questions,
                        key = { _, item -> item.localId }) { index, question ->
                        QuestionItem(
                            index = index + 1,
                            question = question,
                            onUpdateQuestion = { text ->
                                viewModel.setEvent(
                                    DetailProtocolEvent.UpdateQuestionText(
                                        question.localId,
                                        text
                                    )
                                )
                            },
                            onDeleteQuestion = {
                                viewModel.setEvent(DetailProtocolEvent.DeleteQuestion(question.localId))
                            },
                            onAddOption = {
                                viewModel.setEvent(DetailProtocolEvent.AddOption(question.localId))
                            },
                            onUpdateOption = { optionId, text ->
                                viewModel.setEvent(
                                    DetailProtocolEvent.UpdateOptionText(
                                        question.localId,
                                        optionId,
                                        text
                                    )
                                )
                            },
                            onDeleteOption = { optionId ->
                                viewModel.setEvent(
                                    DetailProtocolEvent.DeleteOption(
                                        question.localId,
                                        optionId
                                    )
                                )
                            }
                        )
                    }

                    item {
                        ContentButton(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                            onClick = { viewModel.setEvent(DetailProtocolEvent.AddQuestion) },
                            colors = ButtonDefaults.buttonColors().copy(
                                DefaultTheme.colors.surfaceAlternative,
                            ),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(12.dp),
                                horizontalArrangement = Arrangement.Center,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    imageVector = FeatherIcons.Plus,
                                    contentDescription = null,
                                    tint = DefaultTheme.colors.primary
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    text = stringResource(Res.string.add_question),
                                    style = DefaultTheme.typography.bodyMediumMedium,
                                    color = DefaultTheme.colors.primary
                                )
                            }
                        }
                    }

                    item {
                        Spacer(modifier = Modifier.height(80.dp))
                    }
                }

                Box(
                    modifier = Modifier.fillMaxWidth()
                        .padding(16.dp)
                        .background(DefaultTheme.colors.surface)
                        .navigationBarsPadding()
                ) {
                    UIButton(
                        text = stringResource(Res.string.save),
                        onClick = { viewModel.setEvent(DetailProtocolEvent.Save) },
                        isLoading = state.isSaving
                    )
                }
            }
        }
    )
}

@Composable
fun QuestionItem(
    index: Int,
    question: ProtocolQuestionUi,
    onUpdateQuestion: (String) -> Unit,
    onDeleteQuestion: () -> Unit,
    onAddOption: () -> Unit,
    onUpdateOption: (String, String) -> Unit,
    onDeleteOption: (String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                DefaultTheme.colors.surfaceAlternative,
                RoundedCornerShape(12.dp)
            )
            .border(
                1.dp,
                DefaultTheme.colors.outline.copy(alpha = 0.2f),
                RoundedCornerShape(12.dp)
            )
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = "${stringResource(Res.string.protocol_question_number)}$index",
                style = DefaultTheme.typography.subHeading1,
                color = DefaultTheme.colors.onSurface
            )
            Icon(
                imageVector = FeatherIcons.Trash, // Using Trash
                contentDescription = stringResource(Res.string.protocol_delete_question),
                tint = DefaultTheme.colors.error,
                modifier = Modifier
                    .size(32.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(DefaultTheme.colors.error.copy(alpha = 0.1f))
                    .clickable { onDeleteQuestion() }
                    .padding(6.dp)
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        UISelfTextInput(
            modifier = Modifier.fillMaxWidth(),
            value = question.name,
            valueChanged = onUpdateQuestion,
            label = stringResource(Res.string.protocol_question_text),
            maxLines = 5,
            singleLine = false,
            isOnError = question.error != null,
            errorText = question.error ?: ""
        )

        Spacer(modifier = Modifier.height(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(Res.string.protocol_options_label),
                style = DefaultTheme.typography.bodyMediumMedium,
                color = DefaultTheme.colors.onSurface
            )

            ContentButton(
                modifier = Modifier,
                onClick = onAddOption,
                shape = RoundedCornerShape(8.dp)
            ) {
                Row(
                    modifier = Modifier
                        .border(
                            1.dp, DefaultTheme.colors.primary,
                            RoundedCornerShape(8.dp)
                        )
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = FeatherIcons.Plus,
                        contentDescription = null,
                        tint = DefaultTheme.colors.primary,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(
                        text = stringResource(Res.string.add_option),
                        style = DefaultTheme.typography.labelRegular,
                        color = DefaultTheme.colors.primary
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(8.dp))

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            question.options.forEachIndexed { optIndex, option ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = "${optIndex + 1}.",
                        style = DefaultTheme.typography.bodyMediumRegular,
                        color = DefaultTheme.colors.onSurface,
                        modifier = Modifier.width(24.dp)
                    )

                    Box(modifier = Modifier.weight(1f)) {
                        UISelfTextInput(
                            modifier = Modifier.fillMaxWidth(),
                            value = option.value,
                            label = "${stringResource(Res.string.protocol_option_label)}${optIndex + 1}",
                            valueChanged = { onUpdateOption(option.localId, it) },
                            isOnError = option.error != null,
                            errorText = option.error ?: ""
                        )
                    }

                    Spacer(modifier = Modifier.width(8.dp))

                    Icon(
                        imageVector = FeatherIcons.Trash,
                        contentDescription = stringResource(Res.string.protocol_delete_option),
                        tint = DefaultTheme.colors.error,
                        modifier = Modifier
                            .size(32.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(DefaultTheme.colors.error.copy(alpha = 0.1f))
                            .clickable { onDeleteOption(option.localId) }
                            .padding(6.dp)
                    )
                }
            }
        }
    }
}
